import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ShoppingBag, Package, FileText, Settings, LayoutDashboard, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const emptyPw = { current_password: "", new_password: "", confirm: "" };

export const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pw, setPw] = useState(emptyPw);
  const [savingPw, setSavingPw] = useState(false);

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated successfully");
      setPw(emptyPw);
      setSettingsOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPw(false); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 max-w-[200px]" data-testid="user-menu-trigger">
            <span className="truncate" data-testid="user-email">{user.email}</span>
            <ChevronDown className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-sm" data-testid="user-menu-content">
          <DropdownMenuLabel className="text-xs text-gray-500 font-normal truncate">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {user.role === "admin" && (
            <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
              <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> Admin
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate("/catalog")} data-testid="menu-place-order">
            <ShoppingBag className="w-4 h-4 mr-2" strokeWidth={1.5} /> Place Order
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/catalog")} data-testid="menu-catalog">
            <Package className="w-4 h-4 mr-2" strokeWidth={1.5} /> Catalog
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/invoices")} data-testid="menu-invoices">
            <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> My Invoices
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="menu-settings">
            <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} /> Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Settings</DialogTitle>
            <DialogDescription>Account settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-sm p-4 space-y-3">
              <div className="text-sm font-medium text-gray-900">Change Password</div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-600">Current password</Label>
                <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-sm mt-1" data-testid="current-password-input"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-600">New password</Label>
                <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-sm mt-1" data-testid="new-password-input"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-600">Confirm new password</Label>
                <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-sm mt-1" data-testid="confirm-password-input"/>
              </div>
              <Button size="sm" onClick={changePassword} disabled={savingPw || !pw.current_password || !pw.new_password} className="rounded-sm bg-[#002fa7] hover:bg-[#00227b] w-full" data-testid="change-password-btn">
                {savingPw && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>} Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
